import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  TextField,
  InputAdornment,
  useMediaQuery,
  Divider,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import dayjs from "dayjs";
import axios from "axios";
import DateFilterBar from "../components/DateFilterBar";

const normalizeBaseUrl = (raw) => {
  if (!raw) return "";
  const trimmed = raw.trim().replace(/\/$/, "");
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const STATUS_CONFIG = {
  1: { label: "Pending", color: "warning" },
  2: { label: "Partial", color: "info" },
  3: { label: "Approved", color: "success" },
  4: { label: "Deleted", color: "error" },
};

export default function LeaveRequestHistory() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fullMonth, setFullMonth] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const base = normalizeBaseUrl(localStorage.getItem("saved_sett_api_url") || "");

  const fetchRequests = useCallback(async (from, to) => {
    if (!base || !from || !to) return;
    setLoading(true);
    try {
      const res = await axios.get(`${base}/api/leave-requests/all`, {
        params: {
          from: dayjs(from).format("YYYY-MM-DD"),
          to: dayjs(to).format("YYYY-MM-DD"),
        },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      console.error("Failed to load leave request history:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    if (fromDate && toDate && rows.length === 0 && !loading) {
      fetchRequests(fromDate, toDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const handleApply = (from, to) => {
    fetchRequests(from, to);
  };

  

  const filteredRows = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.EmpName || "").toLowerCase().includes(q) ||
      String(r.EmpId).includes(q)
    );
  });

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 1.5, sm: 2, md: 3 }, display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 1100,
          p: { xs: 2, sm: 3 },
          borderRadius: { xs: 3, sm: 4 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <HistoryIcon sx={{ color: "#00796b" }} />
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={800} color="#004d40">
            Leave Request History
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every leave request submitted by any employee in this range, regardless of approval status.
        </Typography>

        {/* Filter panel — one visually grouped block on mobile, inline row on desktop */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.75, sm: 0 },
            mb: 2.5,
            borderRadius: 3,
            border: { xs: "1px solid rgba(0,121,107,0.15)", sm: "none" },
            bgcolor: { xs: "rgba(0,121,107,0.03)", sm: "transparent" },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="nowrap"
            sx={{ overflowX: { sm: "auto" } }}
            >
            <Box sx={{ flexShrink: 0 }}>
                <DateFilterBar
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                fullMonth={fullMonth}
                setFullMonth={setFullMonth}
                onApply={handleApply}
                />
            </Box>

            <TextField
                size="small"
                fullWidth={isMobile}
                placeholder="Search employee"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                    </InputAdornment>
                ),
                }}
                sx={{ minWidth: { sm: 220 }, flexShrink: 0 }}
            />

            <Button
                variant="contained"
                fullWidth={isMobile}
                onClick={() => handleApply(fromDate, toDate)}
                sx={{ flexShrink: 0 }}
            >
                Apply
            </Button>
            
            </Stack>
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={28} sx={{ color: "#00796b" }} />
          </Box>
        ) : !filteredRows.length ? (
          <Box
            sx={{
              textAlign: "center",
              py: 5,
              px: 2,
              borderRadius: 3,
              bgcolor: "rgba(0,121,107,0.04)",
              border: "1px dashed rgba(0,121,107,0.25)",
            }}
          >
            <Typography variant="body1" fontWeight={700} sx={{ color: "#004d40", mb: 0.5 }}>
              No leave requests in this range
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a wider date range, or check back once a request has been submitted.
            </Typography>
          </Box>
        ) : isMobile ? (
          /* ── MOBILE: stacked cards, one per request ── */
          <Stack spacing={1.5}>
            {filteredRows.map((r) => {
              const sc = STATUS_CONFIG[r.LeaveStatus] || STATUS_CONFIG[1];
              return (
                <Paper
                  key={r.ID}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderLeft: "4px solid #00796b",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <PersonIcon fontSize="small" sx={{ color: "#00796b", flexShrink: 0 }} />
                      <Typography
                        fontWeight={800}
                        color="#004d40"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {r.EmpName || r.EmpId}
                      </Typography>
                    </Stack>
                    <Chip
                      label={sc.label}
                      color={sc.color}
                      size="small"
                      variant="filled"
                      sx={{ fontWeight: 700, flexShrink: 0, ml: 1 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Stack spacing={0.75}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EventIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary">
                        {r.DateIn}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScheduleIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary">
                        {r.TimeIn} → {r.TimeOut}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#00796b" }}>
                      {r.LeaveTypeName || r.LeaveType}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          /* ── DESKTOP: table ── */
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#004d40", color: "white", fontWeight: "bold" }}>Employee</TableCell>
                  <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Date</TableCell>
                  <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Time In</TableCell>
                  <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Time Out</TableCell>
                  <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Leave Type</TableCell>
                  <TableCell sx={{ backgroundColor: "#00796b", color: "white", fontWeight: "bold" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((r) => {
                  const sc = STATUS_CONFIG[r.LeaveStatus] || STATUS_CONFIG[1];
                  return (
                    <TableRow key={r.ID} hover>
                      <TableCell sx={{ fontWeight: 600, color: "#004d40" }}>{r.EmpName || r.EmpId}</TableCell>
                      <TableCell>{r.DateIn}</TableCell>
                      <TableCell>{r.TimeIn}</TableCell>
                      <TableCell>{r.TimeOut}</TableCell>
                      <TableCell>{r.LeaveTypeName || r.LeaveType}</TableCell>
                      <TableCell>
                        <Chip label={sc.label} color={sc.color} size="small" variant="filled" sx={{ fontWeight: 700 }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}